/**
 * Companion to afroyla-collection-hub.js (kept as a 2nd module per that
 * file's core-engine size — plans/20260820-horizon-rebuild/phase-09-collection-
 * hub.md Implementation Step 8). Owns everything that ISN'T axis-matching
 * state: panel/sheet open-close, focus trap, scroll lock, Esc, and the two
 * flavours of Section Rendering fetch (replace-in-place for a price/sort
 * change, append for Load more). Reads/writes the shared `ctx` object the
 * core module stores on `root.__afroylaHubCtx` — see that file's top comment
 * for the division of responsibility.
 *
 * Per M7 (phase-09-collection-hub.md): the mobile sheet's scrim is NOT a
 * close target (deliberate delta from the drop page's sheet, which does close
 * on backdrop click) — only ✕ and "Show N pieces" close it.
 */
(function () {
  'use strict';

  if (window.__aflHubPanelInit) return;
  window.__aflHubPanelInit = true;

  var ROOT_SEL = '[data-afl-collection-hub]';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  /* ---- open/close ------------------------------------------------------------ */

  function openPanel(ctx) {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var panel = qs(ctx.root, '[data-afl-hub-panel="' + (isMobile ? 'mobile' : 'desktop') + '"]');
    if (!panel) return;
    panel.hidden = false;
    qsa(ctx.root, '[data-afl-hub-filter-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'true');
    });
    if (isMobile) {
      document.body.classList.add('afl-drop-scroll-lock');
      var closeBtn = panel.querySelector('[data-afl-hub-filter-close]');
      if (closeBtn) closeBtn.focus();
    }
  }

  function closePanel(ctx) {
    qsa(ctx.root, '[data-afl-hub-panel]').forEach(function (panel) {
      panel.hidden = true;
    });
    qsa(ctx.root, '[data-afl-hub-filter-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('afl-drop-scroll-lock');
  }

  function isPanelOpen(ctx) {
    return qsa(ctx.root, '[data-afl-hub-panel]').some(function (p) {
      return !p.hidden;
    });
  }

  /* Mobile sheet only — the desktop fold-down is in-flow (no overlay), so Tab
     naturally can't "escape" it the way a fixed dialog needs guarding. */
  function trapFocus(ctx, event) {
    if (event.key !== 'Tab') return;
    var sheet = qs(ctx.root, '[data-afl-hub-panel="mobile"]');
    if (!sheet || sheet.hidden) return;
    var focusable = qsa(sheet, FOCUSABLE);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---- Section Rendering fetch: replace (price/sort) or append (Load more) --- */

  function swap(freshRoot, liveRoot, selectors) {
    selectors.forEach(function (sel) {
      var freshNode = freshRoot.querySelector(sel);
      var liveNode = liveRoot.querySelector(sel);
      if (freshNode && liveNode) liveNode.replaceWith(freshNode);
    });
  }

  function mergeIsland(ctx, freshRoot) {
    var freshIslandEl = freshRoot.querySelector('[data-afl-drop-island]');
    if (!freshIslandEl) return;
    try {
      var freshItems = JSON.parse(freshIslandEl.textContent) || [];
      freshItems.forEach(function (item) {
        if (!ctx.itemsByHandle[item.handle]) {
          ctx.items.push(item);
          ctx.itemsByHandle[item.handle] = item;
        }
      });
    } catch (e) {
      /* keep the existing island — nothing to merge */
    }
  }

  function replaceIsland(ctx) {
    var islandEl = ctx.root.querySelector('[data-afl-drop-island]');
    try {
      ctx.items = islandEl ? JSON.parse(islandEl.textContent) || [] : [];
    } catch (e) {
      ctx.items = [];
    }
    ctx.itemsByHandle = {};
    ctx.items.forEach(function (item) {
      ctx.itemsByHandle[item.handle] = item;
    });
  }

  /* H3 review fix: swap in the fresh Load-more block's hidden state/button
     (paginate.next truth for the page that was JUST fetched), but recompute
     the caption's rendered count OURSELVES from the actual DOM — the SSR
     fragment's own count only ever reflects the single page it rendered, not
     the cumulative total appended across every Load-more tap so far. The
     fragment's "of N" total (data-count-total) IS still SSR truth and is
     reused as-is. */
  function morphLoadMore(ctx, freshRoot) {
    var freshLoadmore = freshRoot.querySelector('[data-afl-hub-loadmore]');
    var liveLoadmore = ctx.root.querySelector('[data-afl-hub-loadmore]');
    if (!freshLoadmore || !liveLoadmore) return;

    // paginate.next == nil on the page just fetched — hide/omit the button
    // (H3: the old gate compared one page's count to the collection TOTAL,
    // which stayed false — i.e. visible — even on the actual last page).
    liveLoadmore.hidden = freshLoadmore.hidden;

    // The live button node is no longer replaced wholesale (morphLoadMore
    // mutates in place instead of swapping the block), so its "Loading…"
    // text — set by the click handler before this fetch started — must be
    // restored here too, not just the href/next-page; the click handler's
    // own restore branch only runs when the node it clicked no longer exists.
    var freshBtn = freshLoadmore.querySelector('[data-afl-hub-load-more]');
    var liveBtn = liveLoadmore.querySelector('[data-afl-hub-load-more]');
    if (freshBtn && liveBtn) {
      liveBtn.href = freshBtn.href;
      liveBtn.textContent = freshBtn.textContent;
      liveBtn.setAttribute('data-next-page', freshBtn.getAttribute('data-next-page') || '');
    }

    var freshCaption = freshLoadmore.querySelector('[data-afl-hub-load-caption]');
    var liveCaption = liveLoadmore.querySelector('[data-afl-hub-load-caption]');
    var total = freshCaption ? freshCaption.getAttribute('data-count-total') : null;
    if (liveCaption && total !== null) {
      var rendered = ctx.root.querySelectorAll('[data-afl-hub-card]').length;
      liveCaption.textContent = 'Showing ' + rendered + ' of ' + total + ' pieces';
      liveCaption.setAttribute('data-count-total', total);
    }
  }

  function fetchAndMorph(ctx, state, page, append) {
    // M3 review fix: sequence token so an out-of-order response (double-tap
    // Load more, or a fast filter change while a previous fetch is still in
    // flight) can never append/replace a stale grid over a newer one.
    var mySeq = ++ctx.fetchSeq;
    var url = ctx.urlForState(ctx.collectionUrl, state);
    if (page > 1) url += (url.indexOf('?') === -1 ? '?' : '&') + 'page=' + page;
    var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(ctx.sectionId);

    return fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html || mySeq !== ctx.fetchSeq) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshRoot = doc.querySelector(ROOT_SEL);
        if (!freshRoot) return;

        if (append) {
          var liveGrid = ctx.root.querySelector('[data-afl-hub-grid]');
          var freshCards = freshRoot.querySelectorAll('[data-afl-hub-card]');
          if (liveGrid) freshCards.forEach(function (c) { liveGrid.appendChild(c); });
          mergeIsland(ctx, freshRoot);
          swap(freshRoot, ctx.root, ['[data-afl-hub-result]', '[data-afl-hub-empty]', '[data-afl-hub-tail]']);
          morphLoadMore(ctx, freshRoot);
          ctx.page = page;
        } else {
          swap(freshRoot, ctx.root, ['[data-afl-hub-grid]', '[data-afl-hub-result]', '[data-afl-hub-empty]', '[data-afl-hub-tail]', '[data-afl-hub-loadmore]', '[data-afl-drop-island]']);
          replaceIsland(ctx);
          ctx.page = 1;
        }

        var loadEl = ctx.root.querySelector('[data-afl-hub-loadmore]');
        ctx.hasMorePages = loadEl ? !loadEl.hidden : false;
        ctx.render(ctx, ctx.state);
      })
      .catch(function () {
        /* Fetch failed — the optimistic client render already stands as the
           final state for anything price/sort didn't touch; nothing to roll
           back to. */
      });
  }

  /* ---- init -------------------------------------------------------------------- */

  function init(root) {
    // LOW review fix: check for ctx BEFORE setting the once-guard. Marking
    // the root initialized and THEN bailing on a missing ctx would
    // permanently skip this module for that root if it ever ran before
    // afroyla-collection-hub.js had set `root.__afroylaHubCtx` — a later,
    // legitimate init() call (e.g. from the MutationObserver below) would
    // hit the guard and return without ever wiring the panel.
    var ctx = root.__afroylaHubCtx;
    if (!ctx) return; // afroyla-collection-hub.js must init this root first
    if (root.__afroylaHubPanel) return;
    root.__afroylaHubPanel = true;
    ctx.fetchAndMorph = fetchAndMorph;

    qsa(root, '[data-afl-hub-filter-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        isPanelOpen(ctx) ? closePanel(ctx) : openPanel(ctx);
      });
    });
    qsa(root, '[data-afl-hub-filter-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        closePanel(ctx);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isPanelOpen(ctx)) closePanel(ctx);
      trapFocus(ctx, e);
    });

    // Load more — delegated so a fresh (appended) button never needs rewiring.
    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-afl-hub-load-more]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      e.preventDefault();
      btn.setAttribute('aria-disabled', 'true');
      var prevText = btn.textContent;
      btn.textContent = 'Loading…';
      ctx.fetchAndMorph(ctx, ctx.state, ctx.page + 1, true).then(function () {
        var freshBtn = root.querySelector('[data-afl-hub-load-more]');
        if (freshBtn) freshBtn.removeAttribute('aria-disabled');
        else {
          btn.removeAttribute('aria-disabled');
          btn.textContent = prevText;
        }
      });
    });
  }

  document.querySelectorAll(ROOT_SEL).forEach(init);

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(ROOT_SEL)) init(node);
          else if (node.querySelector && node.querySelector(ROOT_SEL)) node.querySelectorAll(ROOT_SEL).forEach(init);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
